<template>
  <div>
    <v-row class="px-6 mb-4">
      <v-col cols="3">
        <v-text-field
          v-model="searchInputValue"
          prepend-inner-icon="$magnify"
          outlined
          dense
          single-line
          hide-details="auto"
          :placeholder="$t('common.search')"
          clearable
        />
      </v-col>
      <v-col cols="2" offset="5">
        <v-btn outlined @click="clearFilters()" block>
          {{ $t('companies.clearFilters') }}
        </v-btn>
      </v-col>
      <v-col cols="2">
        <v-btn color="primary" depressed @click="openFilterDialog()" block>
          {{ $t('companies.filter') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :items="groups"
      :loading="loading"
      :headers="headers"
      hide-default-footer
      :page="dataTableOptions.page"
      :items-per-page="dataTableOptions.itemsPerPage"
      show-expand
      single-expand
      item-key="id"
      :expanded.sync="expandedRows"
      @update:options="updateOptions"
      @item-expanded="onRowExpanded"
    >
      <template v-slot:[`item.createdAt`]="{ item }">
        {{ item.createdAt | dateFormat() }}
      </template>

      <template v-slot:[`item.owner`]="{ item }">
        <span
          v-if="item.companyShare"
          class="shared-owner-badge"
        >
          {{ formatOwnerName(item) }}
        </span>
        <span v-else>{{ formatOwnerName(item) }}</span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-menu bottom left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-if="item.isOwner" link @click="openEditGroupDialog(item, 'edit')">
              <v-list-item-title>{{ $t('spedilink.groups.edit') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="item.isOwner" link @click="openDeleteGroupDialog(item)">
              <v-list-item-title class="error--text">{{ $t('spedilink.groups.delete') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="!item.isOwner && item.canManageSharing" link @click="openEditGroupDialog(item, 'manageSharing')">
              <v-list-item-title>{{ $t('spedilink.groups.manageSharing') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="!item.isOwner && !item.canManageSharing" link @click="openEditGroupDialog(item, 'view')">
              <v-list-item-title>{{ $t('spedilink.groups.view') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td class="pa-6" :colspan="headers.length">
          <h4 class="mb-3">{{ $t('spedilink.groups.persons') }}</h4>
          <v-data-table
            :headers="personHeaders"
            :items="item.groupPersons || []"
            :loading="item.loadingPersons"
            hide-default-footer
            dense
          >
            <template v-slot:[`item.personActions`]="{ item: person }">
              <v-menu v-if="item.isOwner" bottom left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon x-small v-bind="attrs" v-on="on">
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item link @click="openEditPersonDialog(person, item)">
                    <v-list-item-title>{{ $t('spedilink.persons.edit') }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <template v-slot:no-data>
              <span class="grey--text">—</span>
            </template>
          </v-data-table>
        </td>
      </template>

      <template v-slot:footer v-if="pageCount > 1">
        <v-pagination v-model="dataTableOptions.page" :length="pageCount" />
      </template>
    </v-data-table>

    <SpediLinkGroupsFilterDialog
      v-model="filterDialog"
      :filters="filters"
      @close="filterDialog = false"
      @filter="applyFilter"
    />

    <EditSpediLinkGroupDialog
      v-model="editGroupDialog"
      :group="selectedGroup"
      :mode="editGroupMode"
      @close="editGroupDialog = false"
      @group-edited="onGroupEdited()"
    />

    <DeleteSpediLinkGroupDialog
      v-model="deleteGroupDialog"
      :group="selectedGroup"
      @close="deleteGroupDialog = false"
      @group-deleted="onGroupDeleted()"
    />

    <EditSpediLinkPersonDialog
      v-model="editPersonDialog"
      :person="selectedPerson"
      @close="editPersonDialog = false"
      @person-edited="onPersonEdited()"
    />

  </div>
</template>

<script>
import Service from '@/services'
import SpediLinkGroupsFilterDialog from '@/views/Spedilink/dialogs/SpediLinkGroupsFilterDialog'
import EditSpediLinkGroupDialog from '@/views/Spedilink/dialogs/EditSpediLinkGroupDialog'
import DeleteSpediLinkGroupDialog from '@/views/Spedilink/dialogs/DeleteSpediLinkGroupDialog'
import EditSpediLinkPersonDialog from '@/views/Spedilink/dialogs/EditSpediLinkPersonDialog'

export default {
  name: 'SpediLinkGroupsTable',
  components: {
    SpediLinkGroupsFilterDialog,
    EditSpediLinkGroupDialog,
    DeleteSpediLinkGroupDialog,
    EditSpediLinkPersonDialog
  },
  data () {
    return {
      groups: [],
      loading: false,
      pageCount: 1,
      searchInputValue: '',
      searchTimeout: null,
      expandedRows: [],
      dataTableOptions: {
        page: 1,
        itemsPerPage: 15
      },
      sortProperties: [],
      filters: {
        name: '',
        description: ''
      },
      filterDialog: false,
      selectedGroup: {},
      editGroupDialog: false,
      editGroupMode: 'edit',
      deleteGroupDialog: false,
      selectedPerson: {},
      parentGroupRef: null,
      editPersonDialog: false,
      headers: [
        { text: this.$t('spedilink.groups.name'), value: 'name' },
        { text: this.$t('spedilink.groups.description'), value: 'description' },
        { text: this.$t('spedilink.groups.personsCount'), value: 'userCount', sortable: false },
        { text: this.$t('spedilink.groups.owner'), value: 'owner', sortable: false },
        { text: this.$t('spedilink.groups.createdAt'), value: 'createdAt' },
        { text: '', align: 'end', width: '75', value: 'actions', sortable: false },
        { text: '', value: 'data-table-expand', sortable: false }
      ],
      personHeaders: [
        { text: this.$t('spedilink.groups.personFirstName'), value: 'firstName' },
        { text: this.$t('spedilink.groups.personLastName'), value: 'lastName' },
        { text: this.$t('spedilink.groups.personEmail'), value: 'email' },
        { text: this.$t('spedilink.groups.personPhone'), value: 'phone' },
        { text: '', align: 'end', width: '60', value: 'personActions', sortable: false }
      ]
    }
  },
  async created () {
    await this.loadGroups()
  },
  watch: {
    searchInputValue () {
      this.dataTableOptions.page = 1
      this.collapseAllRows()
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadGroups()
      }, 500)
    },
    'dataTableOptions.page' () {
      this.collapseAllRows()
      this.loadGroups()
    }
  },
  methods: {
    collapseAllRows () {
      this.expandedRows = []
    },
    buildSearchParam () {
      const parts = []
      if (this.searchInputValue) parts.push(this.searchInputValue)
      if (this.filters.name) parts.push(this.filters.name)
      if (this.filters.description) parts.push(this.filters.description)
      return parts.join(' ').trim()
    },
    async loadGroups () {
      this.loading = true
      try {
        const params = {
          page: this.dataTableOptions.page,
          pageSize: this.dataTableOptions.itemsPerPage
        }
        const search = this.buildSearchParam()
        if (search) params.search = search
        if (this.sortProperties.length > 0) {
          params.sortableProperties = JSON.stringify(this.sortProperties)
        }
        const { data } = await Service.get('spedilink/SpediLinkGroups/GetList', params)
        this.groups = data.results.map(g => ({ ...g, groupPersons: null, loadingPersons: false }))
        this.pageCount = Math.ceil(data.count / this.dataTableOptions.itemsPerPage)
      } catch (e) {
      } finally {
        this.loading = false
      }
    },
    async loadGroupPersons (group) {
      group.loadingPersons = true
      try {
        const { data } = await Service.get(`spedilink/SpediLinkGroups/getusers/${group.id}/users`)
        group.groupPersons = data
      } catch (e) {
        group.groupPersons = []
      } finally {
        group.loadingPersons = false
      }
    },
    openFilterDialog () {
      this.filterDialog = true
    },
    applyFilter (appliedFilters) {
      this.filters = appliedFilters
      this.dataTableOptions.page = 1
      this.collapseAllRows()
      this.loadGroups()
      this.filterDialog = false
    },
    clearFilters () {
      this.searchInputValue = ''
      this.filters = { name: '', description: '' }
      this.dataTableOptions.page = 1
      this.collapseAllRows()
      this.loadGroups()
    },
    updateOptions (options) {
      this.sortProperties = options.sortBy.map((element, index) => ({
        propertyName: element,
        priority: index,
        type: options.sortDesc[index] ? 1 : 0
      }))
    },
    async onRowExpanded ({ item, value }) {
      if (value && item.groupPersons === null) {
        await this.loadGroupPersons(item)
      }
    },
    openEditGroupDialog (group, mode = 'edit') {
      this.selectedGroup = group
      this.editGroupMode = mode
      this.editGroupDialog = true
    },
    formatOwnerName (group) {
      const first = group.ownerFirstName || ''
      const last = group.ownerLastName || ''
      return `${first} ${last}`.trim()
    },
    async onGroupEdited () {
      this.editGroupDialog = false
      await this.loadGroups()
    },
    openDeleteGroupDialog (group) {
      this.selectedGroup = group
      this.deleteGroupDialog = true
    },
    async onGroupDeleted () {
      this.deleteGroupDialog = false
      if (this.groups.length === 1 && this.dataTableOptions.page > 1) {
        this.dataTableOptions.page -= 1
      } else {
        await this.loadGroups()
      }
    },
    openEditPersonDialog (person, parentGroup) {
      this.selectedPerson = person
      this.parentGroupRef = parentGroup
      this.editPersonDialog = true
    },
    async onPersonEdited () {
      this.editPersonDialog = false
      if (this.parentGroupRef) {
        this.parentGroupRef.groupPersons = null
        await this.loadGroupPersons(this.parentGroupRef)
      }
    }
  }
}
</script>

<style scoped>
.shared-owner-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: #4caf50;
  color: #fff;
}
</style>
